/**
 * validation.js — shared phone and email formatting + validation helpers
 *
 * Phone format enforced:  XXX-XXX-XXXX  (area code dash exchange dash number)
 * Email validation:       must contain @ and a recognised TLD (.com, .net, .org, etc.)
 */

// ─── Phone ────────────────────────────────────────────────────────────────────

/**
 * formatPhone — strips non-digits then inserts dashes as the user types.
 * Called in onChange so the field always displays XXX-XXX-XXXX.
 *
 * @param {string} raw  — whatever the user typed / pasted
 * @returns {string}    — formatted string, max 12 chars (XXX-XXX-XXXX)
 */
export function formatPhone(raw) {
  // Keep only digits, cap at 10
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
}

/**
 * validatePhone — returns an error string or '' if valid.
 * Accepts blank (optional fields) — callers decide whether blank is allowed.
 */
export function validatePhone(value) {
  if (!value || value.trim() === '') return '';  // blank = optional, caller handles required
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 10) return 'Phone must be 10 digits (XXX-XXX-XXXX)';
  return '';
}

// ─── Email ────────────────────────────────────────────────────────────────────

/**
 * validateEmail — returns an error string or '' if valid.
 * Checks for: local part, @, domain, dot, and a TLD of 2-6 chars.
 */
export function validateEmail(value) {
  if (!value || value.trim() === '') return '';  // blank = optional
  // Must have exactly one @, something before it, a dot after it, and a real TLD
  const re = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,6}$/;
  if (!re.test(value.trim())) return 'Enter a valid email (e.g. name@example.com)';
  return '';
}

/**
 * displayPhone — formats a stored phone string for display.
 * Handles already-formatted (XXX-XXX-XXXX) and raw 10-digit strings.
 */
export function displayPhone(value) {
  if (!value) return '';
  return formatPhone(value);
}

// ─── vCard ────────────────────────────────────────────────────────────────────

/**
 * M24: Map a phone label to a vCard TEL TYPE parameter.
 */
const _LABEL_TO_TEL_TYPE = {
  mobile: 'CELL,VOICE',
  cell: 'CELL,VOICE',
  home: 'HOME,VOICE',
  work: 'WORK,VOICE',
  office: 'WORK,VOICE',
  fax: 'FAX',
  primary: 'WORK,VOICE',
};

/**
 * buildVCard — returns a vCard 3.0 string ready to encode in a QR code or
 * download as a .vcf file.
 *
 * M24: now accepts optional `phones` array [{label, number}] for multi-phone.
 * Falls back to single `phone` for backwards compatibility.
 *
 * @param {{ name, company, phone, phones, email, notes }} contact
 * @returns {string}
 */
export function buildVCard({ name = '', company = '', phone = '', phones = [], email = '', notes = '' }) {
  // vCard 3.0 spec — CRLF line endings required
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
  ];

  if (name)    lines.push(`FN:${name}`);
  if (name)    lines.push(`N:${name.includes(' ') ? name.split(' ').slice(1).join(' ') : ''};${name.split(' ')[0]};;;`);
  if (company) lines.push(`ORG:${company}`);

  // M24: emit all phones from the phones array, fall back to single phone
  if (phones && phones.length > 0) {
    for (const p of phones) {
      if (!p.number) continue;
      const telType = _LABEL_TO_TEL_TYPE[(p.label || '').toLowerCase()] || 'WORK,VOICE';
      lines.push(`TEL;TYPE=${telType}:${p.number.replace(/\D/g, '')}`);
    }
  } else if (phone) {
    lines.push(`TEL;TYPE=WORK,VOICE:${phone.replace(/\D/g, '')}`);
  }

  if (email)   lines.push(`EMAIL;TYPE=WORK:${email}`);
  if (notes)   lines.push(`NOTE:${notes.replace(/\n/g, '\\n')}`);

  lines.push('END:VCARD');
  return lines.join('\r\n');
}
